import { Box, BoxProps, ColorOptions, Loader, Text, TextProps } from '@jfs/ui-new'
import { FC, useState } from 'react'
import { useStores } from '../../data/store'
import { convertHexToRGBA } from '../../utils'

interface ButtonProps {
  type?: keyof typeof buttonTypes | Array<keyof typeof buttonTypes>
  onPress: () => void | Promise<any> | Array<() => void | Promise<any>>
  isLoading?: boolean | Array<boolean>
  disabled?: boolean | Array<boolean>
  title?: string | Array<string>
  isRound?: boolean | Array<boolean>
  isFull?: boolean | Array<boolean>
  testID?: string
}

const defaultStyles = {
  text: (textColor: string) =>
    ({
      color: textColor,
      fontFamily: 'jioType-medium',
    } as TextProps),
  container: (isRound: boolean, isFull: boolean, brandColor: string) =>
    ({
      height: 'MEDIUM_2',
      width: isFull ? undefined : 177,
      justifyContent: 'center',
      alignSelf: isFull ? 'stretch' : undefined,
      alignItems: 'center',
      backgroundColor: brandColor,
      borderRadius: isRound ? 24 : 'MEDIUM',
      underlayColor: brandColor,
    } as BoxProps),
  disabledOpacity: 0.5,
}

const buttonTypes = {
  primary: {
    ...defaultStyles,
  },
  secondary: {
    ...defaultStyles,
    container: (isRound: boolean, isFull: boolean, brandColor: string, bodyBgColor: string) =>
      ({
        height: 'MEDIUM_2',
        width: isFull ? undefined : 'HUGE',
        justifyContent: 'center',
        alignSelf: isFull ? 'stretch' : undefined,
        alignItems: 'center',
        borderRadius: isRound ? 24 : 'MEDIUM',
        underlayColor: brandColor,
        borderColor: brandColor,
        borderWidth: 1,
        backgroundColor: convertHexToRGBA(bodyBgColor, 5) as ColorOptions,
      } as BoxProps),
    text: (textColor: string, isPressed: boolean, brandColor: string) =>
      ({
        fontSize: 12,
        fontWeight: '700',
        color: isPressed ? textColor : brandColor,
      } as TextProps),
  },
  specific: {
    ...defaultStyles,
    container: (isRound: boolean, isFull: boolean, brandColor: string, bodyBgColor: string) =>
      ({
        height: 'MEDIUM',
        width: isFull ? undefined : '90',
        justifyContent: 'center',
        alignSelf: isFull ? 'stretch' : undefined,
        alignItems: 'center',
        borderRadius: isRound ? 8 : 'MEDIUM',
        underlayColor: brandColor,
        borderColor: brandColor,
        padding: 5,
        borderWidth: 1,
        backgroundColor: convertHexToRGBA(bodyBgColor, 5) as ColorOptions,
      } as BoxProps),
    text: (textColor: string, isPressed: boolean, brandColor: string) =>
      ({
        fontSize: 10,
        fontWeight: '700',
        textAlign: 'center',
        color: isPressed ? textColor : brandColor,
      } as TextProps),
  },
}

export const Button: FC<ButtonProps> = (props) => {
  const { children, testID } = props
  const {
    type = 'primary',
    isLoading: propsLoading = false,
    onPress,
    title,
    isRound = false,
    isFull = false,
    disabled = false,
  } = props
  const isInternalState = propsLoading === undefined
  const [pressState, setPressState] = useState(false)
  const [isLoading, setLoading] = useState(false)
  // const { bgColor, textColor } = useStores((root) => ({
  //   bgColor: root.config.merchantTheme.headerBackground,
  //   textColor: root.config.merchantTheme.headerText
  // )})

  const { brandColor, textColor, bodyBgColor } = useStores((root) => ({
    brandColor: root.config.merchantTheme.brandColor,
    textColor: root.config.merchantTheme.headingText,
    bodyBgColor: root.config.merchantTheme.bodyBgColor,
    bodyTextColor: root.config.merchantTheme.bodyTextColor,
  }))

  const onPressInternal = async () => {
    if (isLoading || disabled) {
      return
    }
    try {
      setLoading(true)
      if (onPress) {
        await onPress()
      }
      setLoading(false)
    } catch (ex) {
      setLoading(false)
      throw ex
    }
  }
  const contents = children ? (
    children
  ) : (
    <>
      <Text {...buttonTypes[type as keyof typeof buttonTypes].text(textColor, pressState, brandColor)}>{title}</Text>
    </>
  )
  return (
    <Box
      justifyContent="center"
      alignItems="center"
      testID={testID}
      disabled={disabled}
      onPress={onPressInternal}
      onPressIn={() => setPressState(true)}
      onPressOut={() => setPressState(false)}
      opacity={disabled ? buttonTypes[type as keyof typeof buttonTypes].disabledOpacity : 1}
      {...buttonTypes[type as keyof typeof buttonTypes].container(
        isRound as boolean,
        isFull as boolean,
        brandColor as string,
        bodyBgColor as string,
      )}
      height={type === 'specific' ? '36' : '48'}>
      {(isInternalState ? isLoading : propsLoading) ? (
        <Loader
          testID="buttonLoader"
          color={type === 'primary' ? (bodyBgColor as ColorOptions) : (brandColor as ColorOptions)}
        />
      ) : (
        contents
      )}
    </Box>
  )
}
